import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  Plus,
  Trash,
  PencilSimple,
  Eye,
  EyeSlash,
  Check,
  X,
  Circle,
  CaretDown,
  Video,
  MapPin,
  Shield,
  ShieldCheck,
  User as UserIcon,
} from '@phosphor-icons/react';
import axios from 'axios';
import { useAuthStore } from '@/store';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UsersManagement = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'viewer',
    allowed_devices: [],
  });

  // Fetch users
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Fetch devices for selection
  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevices(response.data);
    } catch (err) {
      console.error('Error fetching devices:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDevices();
  }, []);

  // Create/Update user
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingUser) {
        // Update
        await axios.put(`${BACKEND_URL}/api/users/${editingUser.id}`, {
          name: formData.name,
          role: formData.role,
          allowed_devices: formData.allowed_devices,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create
        await axios.post(`${BACKEND_URL}/api/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (err) {
      console.error('Error saving user:', err);
      alert(err.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  // Delete user
  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Error al eliminar usuario');
    }
  };

  // Edit user
  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      allowed_devices: user.allowed_devices || [],
    });
    setShowModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'viewer',
      allowed_devices: [],
    });
  };

  // Toggle device selection
  const toggleDevice = (imei) => {
    setFormData(prev => ({
      ...prev,
      allowed_devices: prev.allowed_devices.includes(imei)
        ? prev.allowed_devices.filter(d => d !== imei)
        : [...prev.allowed_devices, imei]
    }));
  };

  // Select all devices
  const selectAllDevices = () => {
    setFormData(prev => ({
      ...prev,
      allowed_devices: devices.map(d => d.imei)
    }));
  };

  // Clear device selection
  const clearDevices = () => {
    setFormData(prev => ({
      ...prev,
      allowed_devices: []
    }));
  };

  // Get role badge
  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return (
          <span className="px-2 py-1 rounded-full bg-primary/20 text-primary text-xs font-medium flex items-center gap-1">
            <ShieldCheck size={12} /> Admin
          </span>
        );
      case 'operator':
        return (
          <span className="px-2 py-1 rounded-full bg-secondary/20 text-secondary text-xs font-medium flex items-center gap-1">
            <Shield size={12} /> Operador
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded-full bg-gray-200 text-gray-500 text-xs font-medium flex items-center gap-1">
            <UserIcon size={12} /> Visor
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="users-page">
      {/* Header */}
      <header className="h-16 border-b border-gray-200 flex items-center justify-between px-4 bg-white/95 backdrop-blur sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-bold flex items-center gap-2">
              <Users size={20} className="text-primary" />
              Gestión de Usuarios
            </h1>
            <p className="text-xs text-gray-500">
              Administra los usuarios de tu plataforma
            </p>
          </div>
        </div>
        
        <button
          onClick={() => {
            resetForm();
            setEditingUser(null);
            setShowModal(true);
          }}
          className="h-9 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
          data-testid="add-user-btn"
        >
          <Plus size={18} /> Nuevo Usuario
        </button>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Info Box */}
        <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
          <h3 className="font-medium mb-2 flex items-center gap-2">
            <Shield size={18} /> Sistema de Permisos
          </h3>
          <ul className="text-sm text-gray-500 space-y-1">
            <li>• <strong>Admin:</strong> Acceso completo a todos los dispositivos y funciones</li>
            <li>• <strong>Operador:</strong> Puede ver y enviar comandos a dispositivos asignados</li>
            <li>• <strong>Visor:</strong> Solo puede ver ubicación y video de dispositivos asignados</li>
          </ul>
        </div>

        {/* Users List */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Rol</th>
                  <th>Dispositivos</th>
                  <th>Estado</th>
                  <th>Último acceso</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? users.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </td>
                    <td>{getRoleBadge(user.role)}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <MapPin size={14} className="text-gray-500" />
                        <span className="text-sm">
                          {user.allowed_devices?.length || 0} dispositivos
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_active !== false 
                          ? 'bg-secondary/20 text-secondary' 
                          : 'bg-destructive/20 text-destructive'
                      }`}>
                        {user.is_active !== false ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="text-sm text-gray-500">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString('es-ES') : 'Nunca'}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <PencilSimple size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-2 rounded-lg hover:bg-destructive/20 text-destructive transition-colors"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-gray-500">
                      <Users size={40} className="mx-auto mb-3" />
                      <p>No hay usuarios creados</p>
                      <p className="text-sm">Crea un usuario para dar acceso a tu plataforma</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-gray-200 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto fade-in" data-testid="user-modal">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-bold text-lg">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingUser(null);
                }}
                className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Email */}
              <div>
                <label className="text-sm font-medium mb-1 block">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  disabled={editingUser}
                  data-testid="user-email-input"
                  className="w-full h-10 px-3 rounded-lg bg-gray-100 border border-gray-200 text-sm disabled:opacity-50"
                  placeholder="usuario@email.com"
                />
              </div>

              {/* Password */}
              {!editingUser && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Contraseña</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      data-testid="user-password-input"
                      className="w-full h-10 px-3 pr-10 rounded-lg bg-gray-100 border border-gray-200 text-sm"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              )}

              {/* Name */}
              <div>
                <label className="text-sm font-medium mb-1 block">Nombre</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="user-name-input"
                  className="w-full h-10 px-3 rounded-lg bg-gray-100 border border-gray-200 text-sm"
                  placeholder="Nombre del usuario"
                />
              </div>

              {/* Role */}
              <div>
                <label className="text-sm font-medium mb-1 block">Rol</label>
                <div className="relative">
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    data-testid="user-role-select"
                    className="w-full h-10 px-3 pr-8 rounded-lg bg-gray-100 border border-gray-200 text-sm appearance-none"
                  >
                    <option value="viewer">Visor - Solo ver</option>
                    <option value="operator">Operador - Ver y comandos</option>
                    <option value="admin">Admin - Acceso completo</option>
                  </select>
                  <CaretDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
                </div>
              </div>

              {/* Device Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Dispositivos permitidos</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllDevices}
                      className="text-xs text-primary hover:underline"
                    >
                      Seleccionar todos
                    </button>
                    <span className="text-gray-500">|</span>
                    <button
                      type="button"
                      onClick={clearDevices}
                      className="text-xs text-gray-500 hover:underline"
                    >
                      Limpiar
                    </button>
                  </div>
                </div>
                
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  {devices.length > 0 ? devices.map((device) => (
                    <label
                      key={device.imei}
                      className="flex items-center gap-3 p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-0"
                    >
                      <input
                        type="checkbox"
                        checked={formData.allowed_devices.includes(device.imei)}
                        onChange={() => toggleDevice(device.imei)}
                        className="w-4 h-4 rounded border-gray-200"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{device.deviceName || device.imei}</p>
                        <p className="text-xs text-gray-500">{device.imei}</p>
                      </div>
                      <Video size={16} className="text-gray-500" />
                    </label>
                  )) : (
                    <p className="p-4 text-center text-gray-500 text-sm">
                      No hay dispositivos disponibles
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.allowed_devices.length} dispositivos seleccionados
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingUser(null);
                  }}
                  className="flex-1 h-10 rounded-lg bg-gray-200 text-gray-500 font-medium hover:bg-gray-200/80 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 h-10 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  data-testid="save-user-btn"
                >
                  {loading ? (
                    <Circle size={18} className="spinner" />
                  ) : (
                    <>
                      <Check size={18} />
                      {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
